import * as React from 'react';

import { getAuthMode } from 'js/lib/authModeDeepLinkUtils';
import useRouter from 'js/lib/useRouter';
import { get as getUser, isAuthenticatedUser } from 'js/lib/user';

import { ENTERPRISE_MODAL_NAMES } from 'bundles/unified-common/constants/enterprise';
import {
  useEnterpriseProductFeatures,
  useEnterpriseProductFeaturesLoading,
} from 'bundles/unified-common/contexts/EnterpriseProductFeaturesContext';
import { useThirdPartyOrganization } from 'bundles/unified-common/contexts/org-scoped/ThirdPartyOrganizationContext';
import { useShowEnterpriseTermsOfUseModal } from 'bundles/unified-common/hooks/useShowEnterpriseTermsOfUseModal';
import EnterpriseOrgScopedModalManagerContext from 'bundles/unified-common/providers/org-scoped/modals/EnterpriseOrgScopedModalManagerContext';
import useIncomingProgramAllowlistEligibility from 'bundles/unified-home-common/hooks/useIncomingProgramAllowlistEligibility';
import useOrgScopedProgramMembership from 'bundles/unified-home-common/hooks/useOrgScopedProgramMembership';

type Props = {
  children: React.ReactNode;
};

/**
 * Provider for org-scoped enterprise modals.
 *
 * This provider determines which modal (if any) should be displayed to the user
 * at the org level. Currently supports:
 * - EnterpriseTermsOfUseModal
 * - OrgScopedForcedSSORecoveryEmailModal (ADD_EMAIL)
 *
 * For program-scoped modals, see bundles/unified-common/providers/EnterpriseModalManagerProvider.tsx
 *
 * TODO: The following modals from EnterpriseModalManagerProvider are program-scoped
 * and would need program membership data to be migrated here:
 * - ProgramAccessModal (requires programMembership, program data)
 * - AutoenrolledCoursesModal (requires programMembership, autoEnrolledCourses)
 * - ProgramAtCapacityModal (requires program join error state)
 */
const EnterpriseOrgScopedModalManagerProvider = ({ children }: Props) => {
  const router = useRouter();
  const thirdPartyOrganization = useThirdPartyOrganization();
  const features = useEnterpriseProductFeatures();
  const isFeaturesLoading = useEnterpriseProductFeaturesLoading();
  const isInLoginMode = getAuthMode(router.location.query, router.location.hash) === 'login';

  const [showChangePreviewProgramsModal, setShowChangePreviewProgramsModal] = React.useState(false);
  const [orgAutoJoinErrorCode, setOrgAutoJoinErrorCode] = React.useState<string | null>(null);
  const [orgAutoJoinLoading, setOrgAutoJoinLoading] = React.useState(false);

  // EnterpriseTermsOfUseModal state
  const {
    showEnterpriseTermsOfUseModal,
    setShowEnterpriseTermsOfUseModal,
    isEnterpriseAdmin,
    shouldShowCCPAMarketingConsent,
    shouldShowGDPRMarketingConsent,
    shouldShowThirdPartyTermsOfConsent,
    isLoading: isEnterpriseConsentLoading,
  } = useShowEnterpriseTermsOfUseModal(thirdPartyOrganization);

  // Email-verification modal state (org-scoped). The invited/allowlisted-but-unverified learner
  // sees the same verify-email modal that program home shows, instead of the "No Access" banner.
  // The modal container (OrgScopedEmailVerifyModal) sources the email + refetch from its own hook
  // call (Apollo shares the query cache), so only the show/hide decision lives here.
  const { shouldShowEmailVerification: shouldShowEmailVerificationFromMembership, isLoading: isMembershipLoading } =
    useOrgScopedProgramMembership(getUser()?.id, thirdPartyOrganization);

  // The bulk org-scoped membership query above only ever surfaces real roster memberships and
  // explicit invitations — it has no allowlist awareness, so a domain-allowlisted (no invitation)
  // learner is invisible to it. EnterprisePageGuard preserves the specific programId the learner
  // clicked into through the program->org redirect for exactly this case; look that one program up
  // with the same query + adapter the program-scoped page already uses correctly.
  const incomingProgramId =
    typeof router.location.query.programId === 'string' ? router.location.query.programId : undefined;
  const {
    isAllowlisted: isIncomingProgramAllowlisted,
    isEmailNotVerified: isIncomingProgramEmailNotVerified,
    isLoading: isIncomingProgramLoading,
  } = useIncomingProgramAllowlistEligibility(incomingProgramId);

  const isModalLoading =
    isFeaturesLoading || isEnterpriseConsentLoading || isMembershipLoading || isIncomingProgramLoading;

  const isEnterpriseTnCModalEnabled = features?.enableEnterpriseTermsOfUseModal;

  // NOTE: The program-scoped version in EnterpriseModalManagerProvider also checks:
  // - programMembershipInstance?.isEligibleToJoinProgram
  // For org-scoped pages, we show the modal for all authenticated users since
  // they're browsing at the org level without a specific program context.
  const shouldShowEnterpriseTermsOfUseModal =
    isAuthenticatedUser() && isEnterpriseTnCModalEnabled && showEnterpriseTermsOfUseModal;

  // Show the verify-email modal for an authenticated learner whose email isn't verified, either
  // because they're invited/allowlisted to some program per the bulk org-scoped query, or because
  // the specific incoming program (preserved through the redirect) says they're allowlisted and
  // unverified. The incoming-program branch is gated on enableProgramAutoJoin: once verified,
  // useProgramAutoJoinDomainAllowList is what actually joins the learner to the program, and that
  // hook no-ops when the flag is off, which would otherwise leave the learner verified but unjoined.
  // Guard on both loading states so we don't flash it before data resolves.
  const shouldShowEmailVerification =
    isAuthenticatedUser() &&
    !isMembershipLoading &&
    !isIncomingProgramLoading &&
    (!!shouldShowEmailVerificationFromMembership ||
      (!!features?.enableProgramAutoJoin && isIncomingProgramAllowlisted && isIncomingProgramEmailNotVerified));

  // Determine if ADD_EMAIL modal should be shown (used internally for modalToShow calculation).
  // Note: Unlike program-scoped, we don't have isBrowseOnlyProgram, isAccessModalDismissed,
  // or programMembershipInstance.hasModalToDisplay checks since we're at the org level.
  const shouldShowAddEmailModal = isAuthenticatedUser() && !isInLoginMode;

  // Detect deep link params from LMS/LXP integrations (showMiniModal + productId signals XDP redirect).
  const shouldRedirectToXdp = !!(router.location.query.showMiniModal && router.location.query.productId);

  // Determine which modal to show (priority order matching EnterpriseModalManagerProvider)
  // 1. ENTERPRISE_TERMS_OF_USE (highest priority)
  // 2. PROGRAM_ACCESS (not applicable for org-scoped)
  // 3. PROGRAM_JOIN_ERROR (not applicable for org-scoped)
  // 4. LOGIN (not applicable for org-scoped)
  // 5. EMAIL_VERIFICATION (invited/allowlisted learner with an unverified email)
  // 6. PRODUCT_DESCRIPTION_PAGE_REDIRECT (deep link from LMS → redirect to org-scoped XDP)
  // 7. AUTOENROLLED_COURSES (not applicable for org-scoped)
  // 8. ADD_EMAIL (lowest priority)
  let modalToShow: (typeof ENTERPRISE_MODAL_NAMES)[keyof typeof ENTERPRISE_MODAL_NAMES] | null = null;

  if (shouldShowEnterpriseTermsOfUseModal) {
    modalToShow = ENTERPRISE_MODAL_NAMES.ENTERPRISE_TERMS_OF_USE;
  } else if (shouldShowEmailVerification) {
    modalToShow = ENTERPRISE_MODAL_NAMES.EMAIL_VERIFICATION;
  } else if (shouldRedirectToXdp) {
    modalToShow = ENTERPRISE_MODAL_NAMES.PRODUCT_DESCRIPTION_PAGE_REDIRECT;
  } else if (shouldShowAddEmailModal) {
    modalToShow = ENTERPRISE_MODAL_NAMES.ADD_EMAIL;
  }

  return (
    <EnterpriseOrgScopedModalManagerContext.Provider
      value={{
        modalToShow,
        isLoading: isModalLoading,
        // EnterpriseTermsOfUseModal state
        showEnterpriseTermsOfUseModal,
        setShowEnterpriseTermsOfUseModal,
        isEnterpriseAdmin,
        shouldShowCCPAMarketingConsent,
        shouldShowGDPRMarketingConsent,
        shouldShowThirdPartyTermsOfConsent,
        // ChangePreviewProgramsModal state
        showChangePreviewProgramsModal,
        setShowChangePreviewProgramsModal,
        // Org-scoped auto-join state
        orgAutoJoinErrorCode,
        setOrgAutoJoinErrorCode,
        orgAutoJoinLoading,
        setOrgAutoJoinLoading,
      }}
    >
      {children}
    </EnterpriseOrgScopedModalManagerContext.Provider>
  );
};

export default EnterpriseOrgScopedModalManagerProvider;
